import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, Save, Trash2, Paperclip, Send, Mail, Plus, Printer, AlertTriangle, CheckCircle2, GitMerge, Search } from "lucide-react";
import { trackerGetJob, trackerUpdateJob, trackerAddNote, trackerDeleteJob, trackerNotifyCustomer, trackerListJobs, trackerMergeJobs, STATUS_COLORS, PRIORITY_COLORS, PRODUCT_TAG_COLORS, JOB_STATUS_LIST, JOB_QUEUE_LIST } from "../../../lib/tracker";
import { customersList, customerSave, CUSTOMER_TYPE_COLORS } from "../../../lib/customers";
import { getAdminToken } from "../../../lib/admin";
import AttachmentPreview, { AttachmentThumb } from "./AttachmentPreview";

/* ---------------- LineItemsSection ---------------- */
/* Shop-floor breakdown of one job that contains multiple products (e.g.
   yard signs + business cards + stickers on the same ticket). Each line
   is informational — the job's top-level status drives automation. */
const uuid = () => (crypto?.randomUUID?.() || `line-${Date.now()}-${Math.random().toString(16).slice(2)}`);

const LineItemsSection = ({ lines, onChange }) => {
  const update = (idx, patch) =>
    onChange(lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const remove = (idx) => onChange(lines.filter((_, i) => i !== idx));
  const add = () => onChange([
    ...lines,
    { id: uuid(), product_type: "", quantity: 1, queue: null, status: null, notes: null },
  ]);

  return (
    <div className="rounded-sm p-3" style={{ border: "1px solid var(--ups-border)", background: "#FAFAF9" }} data-testid="line-items-section">
      <div className="flex items-center justify-between mb-2">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">
            Line Items {lines.length > 0 && <span className="normal-case font-normal ml-1">({lines.length})</span>}
          </span>
          <div className="text-[10px] text-[color:var(--ups-subtext)] mt-0.5">
            Optional — use when one ticket contains multiple products (e.g. yard signs + business cards + stickers).
          </div>
        </div>
        <button
          type="button"
          onClick={add}
          className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm flex items-center gap-1"
          style={{ border: "1px solid var(--ups-ink)", background: "var(--ups-brand)", color: "var(--ups-ink)" }}
          data-testid="line-item-add-btn"
        >
          <Plus size={11}/> Add line
        </button>
      </div>

      {lines.length === 0 ? (
        <div className="text-[11px] text-[color:var(--ups-subtext)] py-1">No line items. Click <b>+ Add line</b> if this ticket has multiple products.</div>
      ) : (
        <div className="space-y-2">
          {lines.map((line, idx) => (
            <div key={line.id || idx}
              className="grid grid-cols-[1fr_80px_1fr_1fr_auto] gap-2 items-end p-2 rounded-sm"
              style={{ background: "#fff", border: "1px solid var(--ups-border)" }}
              data-testid={`line-item-row-${idx}`}>
              <label className="block">
                <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Product</span>
                <input
                  type="text"
                  value={line.product_type || ""}
                  onChange={(e) => update(idx, { product_type: e.target.value })}
                  placeholder="e.g. Yard Signs"
                  className="input-swiss w-full px-2 py-1 mt-0.5 rounded-sm text-[12px]"
                  data-testid={`line-item-product-${idx}`}
                />
              </label>
              <label className="block">
                <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Qty</span>
                <input
                  type="number"
                  min={1}
                  value={line.quantity ?? 1}
                  onChange={(e) => update(idx, { quantity: Math.max(1, parseInt(e.target.value, 10) || 1) })}
                  className="input-swiss w-full px-2 py-1 mt-0.5 rounded-sm text-[12px] font-mono"
                  data-testid={`line-item-qty-${idx}`}
                />
              </label>
              <label className="block">
                <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Queue</span>
                <select
                  value={line.queue || ""}
                  onChange={(e) => update(idx, { queue: e.target.value || null })}
                  className="input-swiss w-full px-2 py-1 mt-0.5 rounded-sm text-[12px]"
                  data-testid={`line-item-queue-${idx}`}
                >
                  <option value="">—</option>
                  {JOB_QUEUE_LIST.map((q) => <option key={q} value={q}>{q}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">Status</span>
                <select
                  value={line.status || ""}
                  onChange={(e) => update(idx, { status: e.target.value || null })}
                  className="input-swiss w-full px-2 py-1 mt-0.5 rounded-sm text-[12px]"
                  data-testid={`line-item-status-${idx}`}
                >
                  <option value="">—</option>
                  {JOB_STATUS_LIST.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <button
                type="button"
                onClick={() => remove(idx)}
                className="p-1 rounded-sm hover:bg-[#FEE2E2]"
                aria-label="Remove line"
                data-testid={`line-item-remove-${idx}`}
              >
                <Trash2 size={13}/>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


/**
 * Fetch an admin-protected attachment as a blob (we can't put the admin
 * password into <a href> — browsers don't send custom headers on link clicks).
 * Opens the file in a new tab via object URL.
 */
const openAdminAttachment = async (url, filename) => {
  try {
    const res = await fetch(url, {
      headers: { "x-admin-password": getAdminToken() || "" },
    });
    if (!res.ok) {
      toast.error(`Download failed (${res.status})`);
      return;
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    // Try to open in a new tab; if blocked, force a download.
    const win = window.open(blobUrl, "_blank", "noopener,noreferrer");
    if (!win) {
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename || "attachment";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    // Revoke after a short delay to give the browser time to use the URL.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
  } catch (e) {
    toast.error(`Download failed: ${e.message || e}`);
  }
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
};
const fmtDateShort = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toISOString().split("T")[0]; } catch { return ""; }
};

/** Relative time (e.g. "12 min ago", "3 hr ago", "yesterday"). */
const fmtRelative = (iso) => {
  if (!iso) return "";
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 0) return "in the future";
    const min = Math.round(ms / 60000);
    if (min < 1) return "just now";
    if (min < 60) return `${min} min ago`;
    const hr = Math.round(min / 60);
    if (hr < 24) return `${hr} hr ago`;
    const day = Math.round(hr / 24);
    if (day === 1) return "yesterday";
    if (day < 14) return `${day} days ago`;
    return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "2-digit" });
  } catch { return iso; }
};

/** Inline "Last status email sent" line shown above the Email Customer button. */
const LastSentLine = ({ info }) => {
  if (!info?.sent_at) return null;
  const sent = (info.status || "").startsWith("sent");
  const skipped = (info.status || "").startsWith("skipped");
  const within24h = (() => {
    try { return Date.now() - new Date(info.sent_at).getTime() < 24 * 3600 * 1000; }
    catch { return false; }
  })();
  return (
    <div
      className="mt-2 px-2 py-1.5 rounded-sm flex items-start gap-2"
      style={{
        background: sent ? "#FFFFFF" : skipped ? "#FEF3C7" : "#FEE2E2",
        border: `1px solid ${sent ? "#A8A29E" : skipped ? "#F59E0B" : "#DC2626"}`,
        fontSize: 11,
      }}
      data-testid="last-status-email-line"
      title={info.subject || ""}
    >
      <Mail size={11} className="mt-0.5 shrink-0" style={{ color: sent ? "#16A34A" : skipped ? "#92400E" : "#991B1B" }} />
      <div className="min-w-0 leading-snug">
        <div className="font-bold">
          Last status email{within24h ? <span className="ml-1 px-1 py-0.5 rounded-sm" style={{ background: "#FFB500", color: "#1C1917", fontSize: 9, letterSpacing: ".06em" }}>WITHIN 24H</span> : null}
        </div>
        <div className="font-mono text-[color:var(--ups-subtext)]">
          {fmtRelative(info.sent_at)} · {fmtDate(info.sent_at)}
        </div>
        <div className="font-mono">
          to <span className="font-bold">{info.to || "—"}</span>
          {info.cc && info.cc.length > 0 && (
            <> · cc <span className="font-bold">{info.cc.join(", ")}</span></>
          )}
        </div>
        <div className="font-mono text-[10px]" style={{ color: sent ? "#16A34A" : skipped ? "#92400E" : "#991B1B" }}>
          delivery: {info.status || "—"}{info.sender_name ? ` · signed as ${info.sender_name}` : ""}
        </div>
      </div>
    </div>
  );
};

const CustomerPicker = ({ job, setField, onCustomerLinked }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await customersList({ q, limit: 8 });
        if (!cancelled) setResults(r);
      } catch { /* silent */ }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const linked = !!job.customer_id;
  const linkedColor = (CUSTOMER_TYPE_COLORS[job._linked_type] || CUSTOMER_TYPE_COLORS.retail);

  const pick = (c) => {
    setField("customer_id", c.id);
    setField("customer_name", c.name);
    setField("customer_email", c.primary_email || "");
    setField(
      "customer_type",
      c.type === "ups_store" ? "UPS Store" : c.type === "house_account" ? "House Account" : "RETAIL"
    );
    onCustomerLinked && onCustomerLinked(c);
    setOpen(false);
    setQ("");
  };

  const unlink = () => {
    setField("customer_id", null);
    onCustomerLinked && onCustomerLinked(null);
  };

  const createRetail = async () => {
    if (!job.customer_name || !job.customer_name.trim()) {
      toast.error("Enter a name first");
      return;
    }
    setBusy(true);
    try {
      const created = await customerSave(null, {
        type: "retail",
        name: job.customer_name.trim(),
        primary_email: (job.customer_email || "").trim() || null,
        phone: null,
      });
      pick(created);
      toast.success(`Created retail customer ${created.name}`);
      setCreating(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally { setBusy(false); }
  };

  return (
    <div className="sm:col-span-2 p-3 rounded-sm" style={{ background: "var(--ups-muted)", border: "1px dashed var(--ups-ink)" }} data-testid="job-customer-picker">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] font-bold uppercase tracking-[0.16em]">Customer</div>
        {linked && (
          <button onClick={unlink} className="text-[10px] font-bold uppercase tracking-[0.14em] hover:underline text-stone-600" data-testid="unlink-customer">Unlink</button>
        )}
      </div>
      {linked ? (
        <div className="flex items-start justify-between gap-2 p-2 rounded-sm" style={{ background: "#fff", border: "2px solid var(--ups-ink)" }}>
          <div className="min-w-0">
            <div className="font-display font-bold text-sm">{job.customer_name || "—"}</div>
            <div className="text-[11px] font-mono text-stone-500 truncate">{job.customer_email || "no email"}</div>
            <div className="text-[10px] font-mono text-stone-400 mt-0.5">linked customer · id: {String(job.customer_id).slice(0, 8)}</div>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="relative">
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder="Type to find existing customer (name / email / domain)…"
              className="input-swiss w-full px-3 py-2 rounded-sm text-sm"
              data-testid="customer-picker-search"
            />
            {open && results.length > 0 && (
              <div className="absolute z-20 mt-1 left-0 right-0 max-h-[280px] overflow-auto rounded-sm" style={{ background: "#fff", border: "1px solid var(--ups-ink)" }}>
                {results.map((c) => {
                  const tc = CUSTOMER_TYPE_COLORS[c.type] || CUSTOMER_TYPE_COLORS.retail;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => pick(c)}
                      className="w-full flex items-center justify-between gap-2 p-2 text-left hover:bg-[#FEF3C7] border-b border-stone-100"
                      data-testid={`customer-pick-${c.id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-[13px] truncate">{c.name}</div>
                        <div className="text-[10px] font-mono text-stone-500 truncate">{c.primary_email || "—"}</div>
                      </div>
                      <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.10em] rounded-sm" style={{ background: tc.bg, color: tc.fg }}>{tc.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1 border-t" style={{ borderColor: "var(--ups-border)" }}>
            <span className="text-[10px] font-mono text-stone-500">No match? Fill the manual fields below — or:</span>
            <button
              type="button"
              onClick={createRetail}
              disabled={busy}
              className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.14em] rounded-sm hover:bg-white"
              style={{ border: "1px solid var(--ups-ink)" }}
              data-testid="create-retail-customer"
            >
              {busy ? <Loader2 size={10} className="inline animate-spin mr-1" /> : <Plus size={10} className="inline mr-1" />}
              Create retail customer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[color:var(--ups-subtext)]">{label}</span>
    {children}
  </label>
);

const JobDrawer = ({ jobId, meta, onClose, onSaved, onDeleted }) => {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [noteHtml, setNoteHtml] = useState("");
  const [noteAuthor, setNoteAuthor] = useState("");
  const [noteFiles, setNoteFiles] = useState([]);
  const [noteSaving, setNoteSaving] = useState(false);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyMsg, setNotifyMsg] = useState("");
  const [notifySender, setNotifySender] = useState("");
  const [notifySaving, setNotifySaving] = useState(false);
  // Track the status the job was loaded with so we can detect a transition
  // to "Ready for Pickup" and offer to send the branded email.
  const [originalStatus, setOriginalStatus] = useState(null);
  // Server snapshot — anything different from this marks the job as "dirty".
  const [snapshot, setSnapshot] = useState(null);
  // Fields that auto-save the moment they change (instead of waiting for the
  // Save button). Picked because they're rapid status updates the team makes
  // multiple times a day. Free-text fields stay manual to avoid mid-typing
  // POSTs.
  const AUTOSAVE_FIELDS = ["status", "priority", "queue", "payment_status"];
  // Holds the latest patch + a timeout so multiple back-to-back changes
  // collapse into a single PATCH.
  const autoSaveTimer = useRef(null);
  const pendingAutoPatch = useRef({});
  // "Just saved" toast cooldown for the auto-save flow.
  const [autoSavedTag, setAutoSavedTag] = useState(null);
  // Attachment preview modal — null or { url, filename }
  const [previewAttachment, setPreviewAttachment] = useState(null);
  // Merge picker — choose another job to merge THIS job INTO (this job is the source)
  const [mergePickerOpen, setMergePickerOpen] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const fresh = await trackerGetJob(jobId);
      setJob(fresh);
      setSnapshot(fresh);
      setOriginalStatus(fresh?.status || null);
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load job"); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (jobId) reload(); }, [jobId]);

  // ---- Dirty / auto-save plumbing ------------------------------------------
  // Compares the in-memory job against the server snapshot for the editable
  // fields. AUTOSAVE_FIELDS get pushed silently, everything else flips the
  // "Unsaved changes" warning and prompts on close.
  const EDITABLE_FIELDS = [
    "name", "customer_type", "customer_name", "customer_email", "received_at",
    "due_at", "priority", "sales_poc_id", "owner_ids", "queue", "status",
    "total_cost", "payment_status", "description", "products", "customer_id",
    "tracking_number", "line_items",
  ];
  const dirtyFields = () => {
    if (!job || !snapshot) return [];
    return EDITABLE_FIELDS.filter((k) => JSON.stringify(job[k] ?? null) !== JSON.stringify(snapshot[k] ?? null));
  };
  const manualDirtyFields = () => dirtyFields().filter((f) => !AUTOSAVE_FIELDS.includes(f));
  const isDirty = manualDirtyFields().length > 0;

  // Browser-level guard for tab/window close. Hook must be unconditional so
  // we declare it BEFORE any early returns. The handler itself reads the
  // latest `isDirty` from closure via the dep array.
  useEffect(() => {
    const handler = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes on this job. Are you sure you want to leave?";
        return e.returnValue;
      }
      return undefined;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  if (!jobId) return null;

  // Schedule an auto-save when one of AUTOSAVE_FIELDS changes. We debounce so
  // a rapid sequence (priority → status → queue) collapses into one PATCH.
  const scheduleAutoSave = (patch) => {
    Object.assign(pendingAutoPatch.current, patch);
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      const fields = pendingAutoPatch.current;
      pendingAutoPatch.current = {};
      try {
        const updated = await trackerUpdateJob(jobId, fields);
        setJob((prev) => ({ ...prev, ...updated }));
        setSnapshot(updated);
        const summary = Object.keys(fields).join(", ");
        // Defer the visual tag so it lands on a render AFTER the snapshot/state
        // commit — otherwise React 18 automatic batching collapses both into a
        // single render where the conditional ternary picks the "clean"
        // branch and the green tag never paints.
        setTimeout(() => {
          setAutoSavedTag(`Saved · ${summary}`);
          setTimeout(() => setAutoSavedTag(null), 2500);
        }, 0);
        onSaved && onSaved(updated);

        // Same Ready-for-Pickup courtesy prompt as the manual save path.
        const wasNotPickup = originalStatus !== "Ready for Pickup";
        setOriginalStatus(updated.status);
        if (wasNotPickup && updated.status === "Ready for Pickup" && (updated.customer_email || "").trim()) {
          const confirmed = window.confirm(
            `📧 Status set to "Ready for Pickup".\n\nSend the branded "Order Ready for Pickup" email to ${updated.customer_email}?\n\nIncludes store address + Google Review CTA.`
          );
          if (confirmed) {
            try {
              const res = await trackerNotifyCustomer(jobId, { sender_name: notifySender || "" });
              const sent = res?.status === "sent";
              const ccNote = res?.cc?.length ? ` (cc ${res.cc.join(", ")})` : "";
              if (sent) {
                toast.success(`Pickup-ready email sent to ${updated.customer_email}${ccNote}`);
                await reload();
              } else {
                toast.warning(`Not sent — ${res?.status || "no status"}${ccNote}. Check backend/.env (RESEND_API_KEY / DISABLE_EMAILS)`);
              }
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Pickup email send failed");
            }
          }
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Auto-save failed — click Save to retry.");
      }
    }, 450);
  };

  const setField = (k, v) => {
    setJob((p) => ({ ...p, [k]: v }));
    if (AUTOSAVE_FIELDS.includes(k)) scheduleAutoSave({ [k]: v });
  };

  // Intercept drawer close (Esc / backdrop / X button) when dirty.
  const handleClose = () => {
    if (isDirty) {
      const choice = window.confirm(
        `You have unsaved changes (${manualDirtyFields().join(", ")}).\n\nClick OK to save and close, or Cancel to keep editing.`
      );
      if (!choice) return;
      // Save synchronously-ish then close.
      handleSave().then(() => onClose());
      return;
    }
    onClose();
  };

  const handleSave = async () => {
    if (!job) return;
    setSaving(true);
    try {
      const patch = {
        name: job.name, customer_type: job.customer_type, customer_name: job.customer_name,
        customer_email: job.customer_email, received_at: job.received_at, due_at: job.due_at,
        priority: job.priority, sales_poc_id: job.sales_poc_id, owner_ids: job.owner_ids,
        queue: job.queue, status: job.status, total_cost: job.total_cost,
        payment_status: job.payment_status, description: job.description,
        products: job.products || [],
        customer_id: job.customer_id || null,
        tracking_number: job.tracking_number || null,
        line_items: job.line_items || [],
      };
      const updated = await trackerUpdateJob(jobId, patch);
      setJob(updated);
      setSnapshot(updated);
      toast.success(updated.archived ? `Saved & archived (${updated.status})` : "Saved");
      onSaved && onSaved(updated);

      // If status just transitioned INTO "Ready for Pickup", offer to send
      // the branded pickup-ready email to the customer.
      const transitionedToPickup =
        originalStatus !== "Ready for Pickup" &&
        updated.status === "Ready for Pickup" &&
        (updated.customer_email || "").trim();
      setOriginalStatus(updated.status);
      if (transitionedToPickup) {
        const confirmed = window.confirm(
          `📧 Status set to "Ready for Pickup".\n\n` +
          `Send the branded "Order Ready for Pickup" email to ${updated.customer_email}?\n\n` +
          `Includes store address + Google Review CTA.`
        );
        if (confirmed) {
          try {
            const res = await trackerNotifyCustomer(jobId, { sender_name: notifySender || "" });
            const sent = res?.status === "sent";
            const ccNote = res?.cc?.length ? ` (cc ${res.cc.join(", ")})` : "";
            if (sent) {
              toast.success(`Pickup-ready email sent to ${updated.customer_email}${ccNote}`);
              await reload();
            } else {
              toast.warning(`Not sent — ${res?.status || "no status"}${ccNote}. Check backend/.env (RESEND_API_KEY / DISABLE_EMAILS)`);
            }
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Pickup email send failed");
          }
        }
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this job? This cannot be undone.")) return;
    try { await trackerDeleteJob(jobId); toast.success("Deleted"); onDeleted && onDeleted(jobId); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const handleMergeInto = async (keeperJob) => {
    if (!keeperJob?.id) return;
    if (!window.confirm(
      `Merge Job #${job?.job_number} into Job #${keeperJob.job_number}?\n\n` +
      `This job will be archived with a "Merged into Job #${keeperJob.job_number}" note. ` +
      `Notes, attachments, customer/order links and total will move to the primary.`
    )) return;
    try {
      const res = await trackerMergeJobs(keeperJob.id, [jobId]);
      toast.success(`Merged into Job #${res.keeper_job_number}`);
      setMergePickerOpen(false);
      onDeleted && onDeleted(jobId);  // refresh the parent list
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    }
  };

  const handleAddNote = async () => {
    if (!noteHtml.trim()) { toast.error("Note can't be empty"); return; }
    setNoteSaving(true);
    try {
      const fd = new FormData();
      fd.append("body_html", noteHtml);
      fd.append("author_name", noteAuthor.trim());
      noteFiles.forEach((f) => fd.append("files", f));
      await trackerAddNote(jobId, fd);
      setNoteHtml(""); setNoteFiles([]);
      await reload();
      toast.success("Note added");
    } catch (e) { toast.error(e?.response?.data?.detail || "Add note failed"); }
    finally { setNoteSaving(false); }
  };

  const exec = (cmd) => {
    document.execCommand(cmd, false, null);
    const ed = document.getElementById("note-editor");
    if (ed) setNoteHtml(ed.innerHTML);
  };

  const handleNotifyCustomer = async () => {
    if (!job?.customer_email) {
      toast.error("Add a customer email first.");
      return;
    }
    setNotifySaving(true);
    try {
      const res = await trackerNotifyCustomer(jobId, {
        message: notifyMsg.trim(),
        sender_name: notifySender.trim(),
      });
      const sent = res?.status === "sent";
      const ccNote = res?.cc?.length ? ` (cc ${res.cc.join(", ")})` : "";
      if (sent) {
        toast.success(`Email sent to ${res.recipient}${ccNote}`);
      } else {
        toast.warning(`Not sent — ${res.status}${ccNote}. Check Resend config in backend/.env`);
      }
      setNotifyOpen(false);
      setNotifyMsg("");
      await reload();
      onSaved && onSaved(job);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setNotifySaving(false);
    }
  };

  const team = meta?.team_members || [];

  return (
    <div className="fixed inset-0 z-50" data-testid="job-drawer">
      {previewAttachment && (
        <AttachmentPreview
          attachment={previewAttachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full sm:w-[640px] bg-white shadow-2xl flex flex-col" style={{ borderLeft: "2px solid var(--ups-ink)" }}>
        <div className="flex items-center justify-between px-5 py-4" style={{ background: "var(--ups-ink)", color: "#FFB500" }}>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em]">Job · {job?.job_number || "…"}</div>
            <div className="text-base font-display font-bold mt-0.5">{job?.name || "Loading…"}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (!job?.id) return;
                window.open(`/admin/jobs/${job.id}/ticket`, "_blank", "noopener,noreferrer");
              }}
              disabled={!job?.id}
              className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 rounded-sm hover:bg-white/10 disabled:opacity-40"
              style={{ border: "1px solid #FFB500", color: "#FFB500" }}
              data-testid="job-drawer-print-btn"
              title="Open job ticket in a new tab — Letter (8.5×11)"
            >
              <Printer size={13} /> Print
            </button>
            <button
              onClick={() => {
                if (!job?.id) return;
                window.open(`/admin/jobs/${job.id}/ticket?format=4x6`, "_blank", "noopener,noreferrer");
              }}
              disabled={!job?.id}
              className="px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 rounded-sm hover:bg-white/10 disabled:opacity-40"
              style={{ border: "1px solid #FFB500", color: "#FFB500" }}
              data-testid="job-drawer-print-4x6-btn"
              title="Open job ticket in a new tab — 4×6 thermal label"
            >
              <Printer size={13} /> 4×6
            </button>
            <button onClick={handleClose} className="p-1 hover:bg-white/10 rounded" aria-label="Close" data-testid="job-drawer-close"><X size={18} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-5">
          {loading || !job ? (
            <div className="flex items-center text-[color:var(--ups-subtext)]"><Loader2 className="animate-spin mr-2" size={14} />Loading…</div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Job Name">
                  <input className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" value={job.name || ""} onChange={(e) => setField("name", e.target.value)} data-testid="job-name-input" />
                </Field>
                <Field label="Status">
                  <select className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" value={job.status} onChange={(e) => setField("status", e.target.value)} data-testid="job-status-select">
                    {meta?.statuses?.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="Priority">
                  <select className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" value={job.priority} onChange={(e) => setField("priority", e.target.value)} data-testid="job-priority-select">
                    {meta?.priorities?.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Production Queue">
                  <select className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" value={job.queue || ""} onChange={(e) => setField("queue", e.target.value || null)}>
                    <option value="">—</option>
                    {meta?.queues?.map((q) => <option key={q} value={q}>{q}</option>)}
                  </select>
                </Field>
                <CustomerPicker job={job} setField={setField} />
                <Field label="Customer Type">
                  <select className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" value={job.customer_type || ""} onChange={(e) => setField("customer_type", e.target.value || null)}>
                    <option value="">—</option>
                    {meta?.customer_types?.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Payment Status">
                  <select className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" value={job.payment_status} onChange={(e) => setField("payment_status", e.target.value)}>
                    {meta?.payment_statuses?.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="Customer Name">
                  <input className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" value={job.customer_name || ""} onChange={(e) => setField("customer_name", e.target.value)} />
                </Field>
                <Field label="Customer Email">
                  <input type="email" className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" value={job.customer_email || ""} onChange={(e) => setField("customer_email", e.target.value)} />
                </Field>
                <Field label="Date Received">
                  <input type="date" className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" value={fmtDateShort(job.received_at)} onChange={(e) => setField("received_at", e.target.value)} />
                </Field>
                <Field label="Due Date">
                  <input type="date" className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" value={fmtDateShort(job.due_at)} onChange={(e) => setField("due_at", e.target.value)} />
                </Field>
                <Field label="Sales POC">
                  <select className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" value={job.sales_poc_id || ""} onChange={(e) => setField("sales_poc_id", e.target.value || null)} data-testid="job-sales-poc">
                    <option value="">—</option>
                    {team.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </Field>
                <Field label="Total Cost ($)">
                  <input type="number" min={0} step="0.01" className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm font-mono" value={job.total_cost ?? 0} onChange={(e) => setField("total_cost", parseFloat(e.target.value) || 0)} />
                </Field>
                <Field label="Tracking Number">
                  <input
                    type="text"
                    className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm font-mono"
                    placeholder="Included in the Shipped email if set"
                    value={job.tracking_number || ""}
                    onChange={(e) => setField("tracking_number", e.target.value)}
                    data-testid="job-tracking-number-input"
                  />
                </Field>
              </div>

              {/* Line items — optional per-piece breakdown of one multi-item order */}
              <LineItemsSection
                lines={job.line_items || []}
                onChange={(next) => setField("line_items", next)}
              />

              <Field label="Owners (multiple)">
                <div className="mt-1 flex flex-wrap gap-2 p-2 rounded-sm" style={{ border: "1px solid var(--ups-border)", background: "#fff" }} data-testid="job-owners">
                  {team.map((t) => {
                    const checked = (job.owner_ids || []).includes(t.id);
                    return (
                      <label key={t.id} className="flex items-center gap-1.5 px-2 py-1 rounded-sm cursor-pointer text-[12px]" style={{ background: checked ? "var(--ups-muted)" : "transparent", border: "1px solid var(--ups-border)" }}>
                        <input type="checkbox" checked={checked} onChange={(e) => {
                          const cur = job.owner_ids || [];
                          setField("owner_ids", e.target.checked ? [...cur, t.id] : cur.filter((x) => x !== t.id));
                        }} />
                        {t.name}
                      </label>
                    );
                  })}
                  {team.length === 0 && <span className="text-[12px] text-[color:var(--ups-subtext)]">Add team members first.</span>}
                </div>
              </Field>
              <Field label={`Products (${(job.products || []).length})`}>
                <div className="flex flex-wrap gap-1.5" data-testid="job-products-field">
                  {(meta?.product_tags || []).map((tag) => {
                    const checked = (job.products || []).includes(tag);
                    const c = PRODUCT_TAG_COLORS[tag] || PRODUCT_TAG_COLORS.Other;
                    return (
                      <label
                        key={tag}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-sm cursor-pointer text-[11px] font-bold uppercase tracking-[0.10em] select-none"
                        style={{
                          background: checked ? c.bg : "var(--ups-muted)",
                          color: checked ? c.fg : "var(--ups-subtext)",
                          border: `1px solid ${checked ? c.bg : "var(--ups-border)"}`,
                        }}
                        data-testid={`product-tag-${tag.replace(/\W+/g, "-").toLowerCase()}`}
                      >
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={checked}
                          onChange={(e) => {
                            const cur = job.products || [];
                            setField("products", e.target.checked ? [...cur, tag] : cur.filter((x) => x !== tag));
                          }}
                        />
                        {tag}
                      </label>
                    );
                  })}
                </div>
              </Field>
              <Field label="Description">
                <textarea rows={3} className="input-swiss w-full px-3 py-2 mt-1 rounded-sm text-sm" value={job.description || ""} onChange={(e) => setField("description", e.target.value)} />
              </Field>

              {/* Customer notify */}
              <div className="p-3 rounded-sm" style={{ background: "#FFF7E6", border: "1px solid #FFB500" }} data-testid="notify-customer-block">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.14em]" style={{ color: "#92400E" }}>Customer Comms</div>
                    <div className="text-[13px] mt-0.5">
                      Send a status update to <span className="font-mono font-bold">{job.customer_email || "—"}</span>
                    </div>
                    <div className="text-[11px] text-[color:var(--ups-subtext)] mt-0.5">
                      Uses the current status (<span className="font-bold">{job.status}</span>) — saves a note to the timeline once sent.
                    </div>
                    {job.last_status_email?.sent_at && (
                      <LastSentLine info={job.last_status_email} />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setNotifyOpen((o) => !o)}
                    disabled={!job.customer_email}
                    className="btn-brutal px-3 py-1.5 text-[11px] rounded-sm font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="notify-customer-toggle"
                    title={!job.customer_email ? "Add customer email first" : ""}
                  >
                    <Mail size={12} /> Email customer
                  </button>
                </div>
                {notifyOpen && (
                  <div className="mt-3 pt-3 border-t" style={{ borderColor: "#FFB500" }}>
                    <div className="grid sm:grid-cols-[180px_1fr] gap-2 mb-2">
                      <select
                        value={notifySender}
                        onChange={(e) => setNotifySender(e.target.value)}
                        className="input-swiss px-2 py-1.5 rounded-sm text-[12px]"
                        data-testid="notify-sender-select"
                      >
                        <option value="">— sign as — (optional)</option>
                        {team.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
                      </select>
                      <div className="text-[11px] font-mono text-[color:var(--ups-subtext)] self-center">
                        Subject: <em>[Job {job.job_number}] {job.status}</em>
                      </div>
                    </div>
                    <textarea
                      rows={3}
                      placeholder="Optional personal note to the customer…"
                      value={notifyMsg}
                      onChange={(e) => setNotifyMsg(e.target.value)}
                      className="input-swiss w-full px-3 py-2 rounded-sm text-sm"
                      data-testid="notify-message-input"
                      maxLength={1500}
                    />
                    <div className="mt-2 flex items-center justify-end gap-2">
                      <button type="button" onClick={() => setNotifyOpen(false)} className="px-3 py-1.5 text-[11px] rounded-sm hover:bg-white" style={{ border: "1px solid var(--ups-border)" }}>
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleNotifyCustomer}
                        disabled={notifySaving || !job.customer_email}
                        className="btn-brutal px-3 py-1.5 text-[11px] rounded-sm font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 disabled:opacity-50"
                        data-testid="notify-customer-send"
                      >
                        {notifySaving ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send email
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes timeline */}
              <div>
                <div className="overline mb-2">Notes &amp; Updates ({(job.notes || []).length})</div>
                <div className="space-y-3">
                  {(job.notes || []).slice().reverse().map((n) => (
                    <div key={n.id} className="p-3 rounded-sm" style={{ border: "1px solid var(--ups-border)", background: "#FAFAFA" }} data-testid={`job-note-${n.id}`}>
                      <div className="text-[11px] font-mono text-[color:var(--ups-subtext)] mb-1.5">{n.author_name || "Anonymous"} · {fmtDate(n.created_at)}</div>
                      <div className="text-[13px] leading-relaxed prose-sm" dangerouslySetInnerHTML={{ __html: n.body_html }} />
                      {(n.attachment_names || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {n.attachment_names.map((fn) => {
                            const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/jobs/${jobId}/notes/${n.id}/attachments/${encodeURIComponent(fn)}`;
                            return (
                              <AttachmentThumb
                                key={fn}
                                url={url}
                                filename={fn}
                                onOpen={(att) => setPreviewAttachment(att)}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add a note */}
                <div className="mt-3 p-3 rounded-sm" style={{ background: "var(--ups-muted)", border: "1px dashed var(--ups-ink)" }}>
                  <div className="flex items-center gap-2 mb-2">
                    <select className="input-swiss px-2 py-1 rounded-sm text-[12px]" value={noteAuthor} onChange={(e) => setNoteAuthor(e.target.value)} data-testid="note-author-select">
                      <option value="">— who's posting —</option>
                      {team.map((t) => <option key={t.id} value={t.name}>{t.name}</option>)}
                    </select>
                    <div className="flex gap-1 ml-2">
                      <button type="button" onClick={() => exec("bold")} className="px-2 py-1 text-[11px] font-bold rounded-sm hover:bg-white" title="Bold">B</button>
                      <button type="button" onClick={() => exec("italic")} className="px-2 py-1 text-[11px] italic rounded-sm hover:bg-white" title="Italic">I</button>
                      <button type="button" onClick={() => exec("insertUnorderedList")} className="px-2 py-1 text-[11px] rounded-sm hover:bg-white" title="Bullets">•</button>
                      <button type="button" onClick={() => exec("insertOrderedList")} className="px-2 py-1 text-[11px] rounded-sm hover:bg-white" title="Numbered">1.</button>
                    </div>
                  </div>
                  <div
                    id="note-editor"
                    contentEditable
                    suppressContentEditableWarning
                    onInput={(e) => setNoteHtml(e.currentTarget.innerHTML)}
                    className="min-h-[80px] px-3 py-2 rounded-sm text-[13px] bg-white"
                    style={{ border: "1px solid var(--ups-border)", outline: "none" }}
                    data-testid="note-editor"
                  />
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <label className="text-[11px] font-mono cursor-pointer" data-testid="note-files-label">
                      <Paperclip size={12} className="inline mr-1" />
                      {noteFiles.length === 0 ? "Attach files" : `${noteFiles.length} file(s)`}
                      <input type="file" multiple className="hidden" onChange={(e) => setNoteFiles(Array.from(e.target.files || []))} data-testid="note-files-input" />
                    </label>
                    <button type="button" onClick={handleAddNote} disabled={noteSaving}
                      className="btn-brutal px-3 py-1.5 text-[12px] rounded-sm font-bold uppercase tracking-[0.14em] flex items-center gap-1.5"
                      data-testid="note-save-btn">
                      {noteSaving ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Post note
                    </button>
                  </div>
                </div>
              </div>

              <div className="text-[11px] font-mono text-[color:var(--ups-subtext)] pt-2 border-t border-[color:var(--ups-border)] space-y-1">
                <div>Created: {fmtDate(job.created_at)}</div>
                <div>Updated: {fmtDate(job.updated_at)}</div>
                <div>Status changed: {fmtDate(job.last_status_change_at)}</div>
                {job.order_number && <div>Linked Order: {job.order_number}</div>}
                {job.archived && <div className="font-bold" style={{ color: "var(--ups-accent)" }}>ARCHIVED</div>}
              </div>
            </>
          )}
        </div>
        {job && (
          <div className="border-t border-[color:var(--ups-border)] px-5 py-3 flex items-center justify-between gap-2 bg-[color:var(--ups-muted)]">
            <div className="flex items-center gap-2">
              <button onClick={handleDelete} className="px-3 py-2 text-[12px] rounded-sm hover:bg-[#FEE2E2] font-bold uppercase tracking-[0.14em] flex items-center gap-1.5" style={{ border: "1px solid var(--ups-border)" }} data-testid="job-delete-btn">
                <Trash2 size={12} /> Delete
              </button>
              <button onClick={() => setMergePickerOpen(true)} className="px-3 py-2 text-[12px] rounded-sm hover:bg-white font-bold uppercase tracking-[0.14em] flex items-center gap-1.5" style={{ border: "1px solid var(--ups-border)" }} data-testid="job-merge-into-btn" title="Merge this job into another existing job">
                <GitMerge size={12} /> Merge into…
              </button>
            </div>
            <div className="flex items-center gap-3">
              {/* Live save status — autosaved tag (transient, 2.5s) OR persistent
                  unsaved indicator if non-auto fields are dirty. */}
              {autoSavedTag ? (
                <span
                  className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.12em]"
                  style={{ color: "#16A34A" }}
                  data-testid="job-autosaved-tag"
                >
                  <CheckCircle2 size={12} /> {autoSavedTag}
                </span>
              ) : isDirty ? (
                <span
                  className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-[0.12em] px-2 py-1 rounded-sm"
                  style={{ background: "#FEF3C7", color: "#92400E", border: "1px solid #F59E0B" }}
                  title={`Unsaved: ${manualDirtyFields().join(", ")}`}
                  data-testid="job-dirty-indicator"
                >
                  <AlertTriangle size={11} /> Unsaved changes
                </span>
              ) : (
                <span className="text-[10px] font-mono text-[color:var(--ups-subtext)]" data-testid="job-clean-indicator">
                  ✓ All changes saved
                </span>
              )}
              <button onClick={handleSave} disabled={saving || !isDirty} className="btn-brutal px-4 py-2 text-[12px] rounded-sm font-bold uppercase tracking-[0.14em] flex items-center gap-1.5 disabled:opacity-40" data-testid="job-save-btn">
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save changes
              </button>
            </div>
          </div>
        )}
      </div>
      {mergePickerOpen && job && (
        <MergeIntoPicker
          excludeJobId={jobId}
          onCancel={() => setMergePickerOpen(false)}
          onPick={handleMergeInto}
        />
      )}
    </div>
  );
};

export default JobDrawer;

/* ----------------------------------------------------------------------
 * MergeIntoPicker — typeahead search for any other job to merge THIS job
 * INTO. The other job becomes the keeper; this job is soft-archived.
 * --------------------------------------------------------------------*/
const MergeIntoPicker = ({ excludeJobId, onCancel, onPick }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchIt = async () => {
      if (q.trim().length < 2) { setResults([]); return; }
      setLoading(true);
      try {
        const params = { q: q.trim(), limit: 20 };
        const open = await trackerListJobs({ ...params, archived: false });
        let merged = open;
        if (includeArchived) {
          const arc = await trackerListJobs({ ...params, archived: true });
          merged = [...open, ...arc];
        }
        if (!cancelled) setResults(merged.filter((j) => j.id !== excludeJobId));
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Search failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    const t = setTimeout(fetchIt, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, excludeJobId, includeArchived]);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="merge-into-picker">
      <button className="absolute inset-0 bg-black/50" onClick={onCancel} aria-label="Close" />
      <div className="relative w-full max-w-[640px] rounded-sm overflow-hidden" style={{ background: "#fff", border: "2px solid var(--ups-ink)" }}>
        <div className="flex items-center justify-between px-4 py-3" style={{ background: "var(--ups-ink)", color: "#FFB500" }}>
          <h3 className="font-display text-[18px] font-bold flex items-center gap-2">
            <GitMerge size={18} /> Merge this job into another
          </h3>
          <button onClick={onCancel} className="p-1.5 rounded-sm hover:bg-white/10" aria-label="Close"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--ups-subtext)]" />
            <input
              type="search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by job #, name, customer, order #…"
              className="input-swiss w-full pl-9 pr-3 py-2 rounded-sm text-[13px]"
              data-testid="merge-picker-search"
            />
          </div>
          <label className="flex items-center gap-2 text-[11px] font-mono text-[color:var(--ups-subtext)]">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Include archived jobs
          </label>
          <div className="max-h-[360px] overflow-y-auto" data-testid="merge-picker-results">
            {q.trim().length < 2 && (
              <p className="text-[12px] text-[color:var(--ups-subtext)] text-center py-6">Start typing to search jobs…</p>
            )}
            {q.trim().length >= 2 && loading && (
              <p className="text-[12px] text-[color:var(--ups-subtext)] text-center py-6 flex items-center justify-center gap-2">
                <Loader2 size={12} className="animate-spin" /> Searching…
              </p>
            )}
            {q.trim().length >= 2 && !loading && results.length === 0 && (
              <p className="text-[12px] text-[color:var(--ups-subtext)] text-center py-6">No matching jobs.</p>
            )}
            {results.map((j) => (
              <button
                key={j.id}
                onClick={() => onPick(j)}
                className="w-full text-left p-2.5 rounded-sm hover:bg-[#FFF8E1] block"
                style={{ borderBottom: "1px solid var(--ups-border)" }}
                data-testid={`merge-pick-${j.id}`}
              >
                <div className="text-[13px] font-bold">
                  <span className="font-mono text-[11px] mr-2">{j.job_number}</span>
                  {j.name || "Untitled"}
                  {j.archived && <span className="ml-2 text-[10px] uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-sm bg-stone-200">Archived</span>}
                </div>
                <div className="text-[11px] text-[color:var(--ups-subtext)] truncate">
                  {j.customer_name || "—"} · {j.status} · ${Number(j.total_cost || 0).toFixed(2)}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
